import { LAST_OPENED_REPORT_STORAGE_KEY } from "../types/constants";
import * as logger from "common/logger";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import * as userSelectors from "store/selectors/user/userSelectors";

// Remembers, per user and per browser, the report the user last opened in
// Report Builder so the landing route can reopen it rather than running a
// default report. Kept in local storage, so it does not follow the user to
// another browser.
//
// Storage shape: { [userKey]: reportId }

const readBag = () => {
  try {
    return JSON.parse(
      localStorage.getItem(LAST_OPENED_REPORT_STORAGE_KEY) || "{}"
    );
  } catch (error) {
    logger.info("Failed to read the last opened report", error);
    return {};
  }
};

const writeBag = (bag) => {
  try {
    localStorage.setItem(LAST_OPENED_REPORT_STORAGE_KEY, JSON.stringify(bag));
  } catch (error) {
    logger.info("Failed to write the last opened report", error);
  }
};

export const getLastOpenedReport = (userKey) => {
  if (!userKey) return null;
  return readBag()[userKey] ?? null;
};

export const setLastOpenedReport = (userKey, reportId) => {
  if (!userKey || !reportId) return;
  const bag = readBag();
  if (bag[userKey] === reportId) return;
  writeBag({ ...bag, [userKey]: reportId });
};

// Forgets the remembered report, but only if it is the one given (e.g. the
// report that has just been deleted).
export const clearLastOpenedReport = (userKey, reportId) => {
  if (!userKey) return;
  const bag = readBag();
  if (!(userKey in bag) || (reportId && bag[userKey] !== reportId)) return;
  delete bag[userKey];
  writeBag(bag);
};

export const useLastOpenedReport = () => {
  const userAttributes = useSelector(userSelectors.selectUserAttributes);
  const userKey = userAttributes?.id ?? userAttributes?.email ?? null;

  const getLastOpened = useCallback(() => getLastOpenedReport(userKey), [
    userKey,
  ]);
  const rememberOpened = useCallback(
    (reportId) => setLastOpenedReport(userKey, reportId),
    [userKey]
  );
  const forgetOpened = useCallback(
    (reportId) => clearLastOpenedReport(userKey, reportId),
    [userKey]
  );

  return { getLastOpened, rememberOpened, forgetOpened };
};
